import { lazy, useEffect, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { BackBtn, Loader, NVButton } from '@nv/basic-components'
import { ActionDropdown, AlertBanner, Colon, EditUser, UploadID, useNVModal } from '@nv/components'
import GreenSmCheckIcon from '@nv/images/design/green-sm-check-icon'
import { AdminVoterAuthService, AdminVoterService } from '@nv/services'
import { formatDateLocale } from '@nv/utils'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DetailsTables } from './details_tables/DetailsTables'
import styles from './AdminVoterDetails.module.scss'

const DeclineVoterForm = lazy(() => import('@nv/lazy-loadable/lib/modals/DeclineVoterForm'))
const ApproveVoterForm = lazy(() => import('@nv/lazy-loadable/lib/modals/ApproveVoterForm'))
const NVBasicModal = lazy(() => import('@nv/lazy-loadable/lib/modals/NVBasicModal'))
const ImageModal = lazy(() => import('@nv/lazy-loadable/lib/modals/ImageModal'))
const Note = lazy(() => import('@nv/lazy-loadable/lib/modals/Note'))

export const AdminVoterDetails = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const { voterId } = useParams()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [approvedVoterFailedModalErrorMsg, setApprovedVoterFailedModalErrorMsg] = useState(
    t('admin.onlineRegistration.voterApprovedFailed'),
  )
  const navigate = useNavigate()
  const [editUserModalView, setEditUserModalView] = useState('Edit')
  const [selectedImage, setSelectedImage] = useState(null)
  const { NVModal: ApproveVoterModal, openModal: openApproveVoterModal, closeModal: closeApproveVoterModal } = useNVModal()
  const { NVModal: DeclineVoterModal, openModal: openDeclineVoterModal, closeModal: closeDeclineVoterModal } = useNVModal()
  const { NVModal: ApprovedVoterSuccessModal, openModal: openApprovedVoterSuccessModal } = useNVModal()
  const { NVModal: ApprovedVoterFailedModal, openModal: openApprovedVoterFailedModal } = useNVModal()
  const { NVModal: DeclinedVoterFailedModal, openModal: openDeclinedVoterFailedModal } = useNVModal()
  const { NVModal: LargeImageModal, openModal: openLargeImageModal } = useNVModal()
  const { NVModal: NoteModal, openModal: openNoteModal, closeModal: closeNoteModal } = useNVModal()
  const { NVModal: EditUserModal, openModal: openEditUserModal } = useNVModal()

  const { NVModal: UploadIDModal, openModal: openUploadIDModal, isModalOpen: isUploadIdModalOpen } = useNVModal()

  const electorActionOptions = []

  if (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) {
    electorActionOptions.push({ value: 'Edit', label: t('admin.dropdown.edit') })
  }

  useEffect(() => {
    if (voterId) getUser()
  }, [voterId])

  const getUser = async () => {
    try {
      setLoading(true)
      const resp = await AdminVoterAuthService.getVoterDetails({ voterId: voterId })
      if (resp) {
        setUser(resp)
      }
    } catch (error) {
      console.error('Failed to fetch voter details:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApproveVoter = async (electorId) => {
    setLoading(true)
    const result = await AdminVoterService.approveVoter(user.voterInfo._id, electorId)
    setLoading(false)
    if (result.success) {
      getUser()
      closeApproveVoterModal()
      openApprovedVoterSuccessModal()
    } else if (result.error) {
      const errorMessage = (result.error as any).message
      const errorCode = (result.error as any).errorCode
      if (errorMessage === 'Voter already registered')
        setApprovedVoterFailedModalErrorMsg(t('admin.onlineRegistration.voterPreviouslyRegistered'))
      else if (errorMessage === 'An elector with this elector ID has already registered')
        setApprovedVoterFailedModalErrorMsg(t('admin.onlineRegistration.electorWithIDAlreadyRegistered'))
      else if (errorCode === 'UNLISTED ADDRESS') setApprovedVoterFailedModalErrorMsg(t('admin.onlineRegistration.errors.unlistedAddress'))
      else setApprovedVoterFailedModalErrorMsg(t('admin.onlineRegistration.voterApprovedFailed'))
      openApprovedVoterFailedModal()
    }
  }

  const handleDeclineVoter = async (reason) => {
    const result = await AdminVoterService.declineVoter(user.voterInfo._id, reason)
    if (result.success) {
      getUser()
      closeDeclineVoterModal()
    } else {
      openDeclinedVoterFailedModal()
    }
  }

  const handleMarkAsPendingWithIssue = async () => {
    if (window.confirm('Are you sure you want to mark this application as pending with issue?')) {
      const result = await AdminVoterService.markAsPendingWithIssue(user.voterInfo._id)
      if (result.success) {
        getUser()
      } else {
        alert('Something went wrong.')
      }
    }
  }

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl)
    openLargeImageModal()
  }

  const updateNotes = async (notes) => {
    closeNoteModal()
    const result = await AdminVoterService.updateNotesRegistration(user.voterInfo._id, { notes })
    if (result.success) {
      getUser()
    } else {
      //display error
    }
  }
  const onClickViewPDF = async (i) => {
    const userData = await AdminVoterAuthService.getVoterDetails({ voterId: voterId })
    // @ts-ignore
    const identification = userData.voterInfo.identification[i]
    window.open(identification.fileUrl, '_blank', 'noopener,noreferrer')
  }

  if (loading) return <Loader />
  else if (!user)
    return (
      <div className={styles['container']}>
        <BackBtn /> <div className={styles['details']}>{t('admin.users.noUser')}</div>
      </div>
    )

  return (
    <>
      <UploadIDModal>
        <UploadID onDone={getUser} voterId={voterId} isOpen={isUploadIdModalOpen} />
      </UploadIDModal>
      <EditUserModal>
        <EditUser modalView={editUserModalView} userToEdit={user} onUpdate={() => getUser()} />
      </EditUserModal>
      <ApproveVoterModal>
        <ApproveVoterForm matches={user?.matches} confirm={handleApproveVoter} loading={loading} />
      </ApproveVoterModal>

      <DeclineVoterModal>
        <DeclineVoterForm confirm={handleDeclineVoter} />
      </DeclineVoterModal>

      <ApprovedVoterSuccessModal>
        <NVBasicModal
          title={t('admin.onlineRegistration.approveVoter')}
          text={t('admin.onlineRegistration.voterApprovedAndEmailSent')}
          success={true}
        />
      </ApprovedVoterSuccessModal>
      <ApprovedVoterFailedModal>
        <NVBasicModal title={t('admin.onlineRegistration.approveVoter')} text={approvedVoterFailedModalErrorMsg} error={true} />
      </ApprovedVoterFailedModal>
      <DeclinedVoterFailedModal>
        <NVBasicModal
          title={t('admin.onlineRegistration.declineVoter')}
          text={t('admin.onlineRegistration.voterDeclinedFailed')}
          error={true}
        />
      </DeclinedVoterFailedModal>

      <LargeImageModal>
        <ImageModal image={selectedImage && selectedImage} />
      </LargeImageModal>

      <NoteModal>
        <Note confirm={updateNotes} initialNote={user.voterInfo.notes} />
      </NoteModal>

      <div className={styles['container']}>
        <div className={styles['header']}>
          <BackBtn />
          <h2>
            {t('admin.onlineRegistration.registrationDate')}
            <Colon /> {formatDateLocale(user.createdAt, currentLanguage)}
          </h2>
        </div>

        <div className={styles['details']}>
          {user.voterInfo.status === 'Declined' && (
            <AlertBanner
              text={t('admin.onlineRegistration.electorProfileDeclined')}
              error={true}
              infoPaneData={
                <p>
                  <strong>{t('admin.onlineRegistration.reasonDeclinedLabel')} </strong>
                  {user.voterInfo.declinedReason.text[currentLanguage]}
                </p>
              }
            />
          )}

          {(user.voterInfo.status === 'Pending' || user.voterInfo.status === 'PendingIssue') &&
            (KeycloakAdminService.isSuperAdmin() ||
              KeycloakAdminService.isVBMAdmin() ||
              ((KeycloakAdminService.isContactInfoRoleAdmin() || KeycloakAdminService.isMonitoringAdmin()) &&
                user.votingChannel === 'inPerson')) && (
              <>
                <div className={styles['details__actions']}>
                  <NVButton variant={'primary'} height={50} onClick={() => openDeclineVoterModal()} text={t('buttons.decline')} />
                  <NVButton variant={'action'} height={50} onClick={() => openApproveVoterModal()} text={t('buttons.approve')} />
                </div>
                <span className={styles['details__divider']}></span>
              </>
            )}

          <div className={styles['details__section']}>
            <div className={styles['details__names']}>
              <div className={styles['details__name']}>
                <div className={styles['details__label']}>{t('form.labels.name')}</div>
                <div className={styles['details__text']}>{user.voterInfo.fullName}</div>
              </div>
              <div className={styles['details__name']}>
                <div className={styles['details__label']}>{t('form.labels.dateOfBirth')}</div>
                <div className={styles['details__text']}>{user.voterInfo.dateOfBirth}</div>
              </div>
            </div>
            <div className={styles['status']}>
              {user.votingChannel && user.votingChannel.toLowerCase() !== 'n/a' && (
                <span className={clsx(styles[`votingChannel-label`], styles[`votingChannel-label__${user.votingChannel}`])}>
                  {t(`admin.electorsList.votedChannelOptions.${user.votingChannel}`)}
                </span>
              )}
              {user.voterInfo.status && (
                <span className={clsx(styles['status-label'], [styles[`status-label__${user.voterInfo.status.toLowerCase()}`]])}>
                  {t(`registration.registrationStatus.${user.voterInfo.status.toLowerCase()}`)}
                </span>
              )}
            </div>
          </div>

          <span className={styles['details__divider']}></span>
          {(KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && (
            <div className={styles['details__section']}>
              <div className={styles['details__names']}>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('form.labels.username')}</div>
                  <div className={styles['details__text']}>{user.username}</div>
                </div>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('form.labels.phoneNumber')}</div>
                  <div className={styles['details__text']}>{user.phone}</div>
                </div>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('form.labels.emailAddress')}</div>
                  <div className={styles['details__text']}>{user.email}</div>
                </div>
              </div>
              {electorActionOptions.length ? (
                <div className={styles['details__name']}>
                  <ActionDropdown
                    values={electorActionOptions}
                    onCallback={(choice) => {
                      switch (choice) {
                        default:
                          setEditUserModalView(choice)
                          openEditUserModal()
                      }
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}

          <span className={styles['details__divider']}></span>

          <div className={styles['details__section']}>
            <div className={styles['details__names']}>
              <div className={styles['details__name']}>
                <div className={styles['details__label']}>{t('form.labels.address')}</div>
                <div className={styles['details__text']}>{user.voterInfo.address.fullAddress}</div>
              </div>
              {user.voterInfo.mailingAddress?.fullAddress && (
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('form.labels.mailingAddress')}</div>
                  <div className={styles['details__text']}>{user.voterInfo.mailingAddress?.fullAddress}</div>
                </div>
              )}
              <div className={styles['details__name']}>
                <div className={styles['details__label']}>{t('admin.electorsDetails.eligibleVotingDistricts')}</div>
                {(user.districts?.length || user.address?.districts?.length) && user.address?.ward ? (
                  <div className={styles['details__text']}>
                    {user.districts?.length
                      ? user.districts.map((d) => (d.ward ? `${d.wardType === 'VSD' ? `${d.wardType}-${d.ward}` : ''}` : '')).join('')
                      : user.address?.districts.length
                      ? user.address?.districts
                          .map((d) => (d.ward ? `${d.wardType === 'VSD' ? `${d.wardType}-${d.ward}` : ''}` : ''))
                          .join('')
                      : 'N/A'}
                    <br />
                    {user.schooBoards?.CBE ? `CBE - Calgary City Wards ${user.schooBoards.CBE}` : 'CBE - Not Eligible'}
                    <br />
                    {user.schooBoards?.CCSD ? `CCSD - Calgary City Wards ${user.schooBoards.CCSD}` : 'CCSD - Not Eligible'}
                  </div>
                ) : (user.voterInfo.status === 'Pending' || user.voterInfo.status === 'PendingIssue') &&
                  (KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isMonitoringAdmin()) ? (
                  <NVButton
                    variant='text'
                    text='Check Where Do I Vote App then Add Voting Subdivision'
                    onClick={() => navigate(`/admin/registrant-edit/${voterId}`)}
                  />
                ) : (
                  <span>
                    <strong>Please check this address in the Where Do I Vote App</strong>
                  </span>
                )}
              </div>
            </div>
          </div>

          <span className={styles['details__divider']}></span>

          {!user.voterInfo.knownIdNumber ? null : (
            <>
              <div className={styles['details__section']}>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('registration.driversLicenseNumber')}</div>
                  <div className={styles['details__text']}>{user.voterInfo.knownIdNumber}</div>
                </div>
              </div>
              <span className={styles['details__divider']}></span>
            </>
          )}

          {(KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && (
            <>
              <div className={styles['details__section']}>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('admin.electorsDetails.registeredVotingDistricts')}</div>
                  {user.electionOptIns
                    .split(',')
                    .sort()
                    .map((district, i) => {
                      if (district)
                        return (
                          <div key={i} className={clsx(styles['details__text'], styles['details__icon'])}>
                            <GreenSmCheckIcon />{' '}
                            {district.toLowerCase().includes('none')
                              ? t('registration.schoolboards.none')
                              : district.includes('PSB')
                              ? t('registration.schoolboards.PSB')
                              : district.includes('CSLF')
                              ? t('registration.schoolboards.CSLF')
                              : ''}
                          </div>
                        )
                    })}
                </div>
              </div>
              <span className={styles['details__divider']}></span>
            </>
          )}

          {(KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && (
            <>
              <div className={styles['details__section']}>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('Ballot Options')}</div>
                  {user.ballotOptIns.map((ballot, i) => {
                    if (ballot)
                      return (
                        <div key={i} className={clsx(styles['details__text'], styles['details__icon'])}>
                          <GreenSmCheckIcon />{' '}
                          {ballot.toLowerCase().includes('none')
                            ? t('registration.ballots.ballot3')
                            : ballot.includes('councillor')
                            ? t('registration.ballots.ballot2')
                            : ballot.includes('mayor')
                            ? t('registration.ballots.ballot1')
                            : ''}
                        </div>
                      )
                  })}
                </div>
              </div>
              <span className={styles['details__divider']}></span>
            </>
          )}

          {user.votingChannel === 'mail' && (
            <>
              <div className={styles['details__section']}>
                <div className={styles['details__name']}>
                  <div className={styles['details__label']}>{t('Ballot Delivery')}</div>
                  <div className={styles['details__text']}> {user.voterInfo.voteByMailPickup ? 'Pick Up' : 'Delivery by Mail'}</div>
                </div>
              </div>
              <span className={styles['details__divider']}></span>
            </>
          )}

          {user.voterInfo.identification?.length && (KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()) ? (
            <div className={styles['details__section']}>
              <div className={styles['details__name']}>
                <div className={styles['details__label']}>{t('registration.proofOfIdentity')}</div>
                <div className={styles['details__image-container']}>
                  {user.voterInfo.identification.map((identification, index) =>
                    identification.filename.endsWith('.pdf') ? (
                      <NVButton key={index} text='View PDF' onClick={() => onClickViewPDF(index)} />
                    ) : (
                      <NVButton
                        key={index}
                        variant='iconOnly'
                        onClick={() => handleImageClick(identification.fileUrl)}
                        icon={<img src={identification.fileUrl} alt={'Identification ' + index} />}
                      />
                    ),
                  )}
                </div>
              </div>
              <div className={styles['details__name']}>
                <ActionDropdown
                  values={[{ value: 'UploadId', label: 'Upload Id' }]}
                  onCallback={(choice) => {
                    switch (choice) {
                      default:
                        openUploadIDModal()
                    }
                  }}
                />
              </div>
            </div>
          ) : null}
          <span className={styles['details__divider']}></span>

          {(KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && !user.voterInfo.onElectorsList && (
            <>
              <div className={styles['details__section']}>
                <div className={styles['details__names']}>
                  <div className={styles['details__name']}>
                    <div className={styles['details__label']}>{t('registration.signature.label')}</div>
                    <div className={styles['details__text']}>{user.signature}</div>
                  </div>
                </div>
              </div>
              <span className={styles['details__divider']}></span>
            </>
          )}

          {(KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && (
            <>
              <div className={styles['details__section']}>
                <div className={styles['details__names']}>
                  <div className={styles['details__name']}>
                    <div className={styles['details__label']}>{t('Notes')}</div>
                    <div className={styles['details__text']}>{user.voterInfo.notes}</div>
                  </div>
                </div>
                <div className={styles['details__actions']}>
                  <NVButton
                    variant={'primary'}
                    height={50}
                    onClick={() => {
                      openNoteModal()
                    }}
                    text={t('Add/Edit Notes')}
                  />
                </div>
              </div>
              <span className={styles['details__divider']}></span>
            </>
          )}

          <DetailsTables user={user} />
          {(user.voterInfo.status === 'Pending' || user.voterInfo.status === 'PendingIssue') &&
            (KeycloakAdminService.isSuperAdmin() ||
              KeycloakAdminService.isVBMAdmin() ||
              ((KeycloakAdminService.isContactInfoRoleAdmin() || KeycloakAdminService.isMonitoringAdmin()) &&
                user.votingChannel === 'inPerson')) && (
              <div className={styles['details__actions']}>
                <NVButton
                  variant={'primary'}
                  height={50}
                  onClick={() => navigate(`/admin/registrant-edit/${voterId}`)}
                  text={t('buttons.edit')}
                />
              </div>
            )}
          {user.voterInfo.status === 'Pending' && (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()) && (
            <div className={styles['details__actions']}>
              <NVButton variant={'primary'} height={50} onClick={handleMarkAsPendingWithIssue} text={t('Mark as Pending with Issue')} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
